<template>
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="layout-header">
      <div class="header-left">
        <div class="ticket-info">
          <h2 class="ticket-name">{{ ticketDisplayName }}</h2>
        </div>

        <TabBar :tabs="availableTabs" :active-tab="activeTab" @update:active-tab="handleTabChange" />
      </div>

      <div class="header-right">
        <template v-if="mode === 'create'">
          <button class="button" @click="cancelEdit">Cancel</button>
          <button class="button primary" @click="saveChanges">Create Ticket</button>
        </template>
        <template v-else-if="mode === 'edit'">
          <button class="button" @click="cancelEditMode">Cancel</button>
          <button class="button primary" @click="saveTicketChanges">Save Changes</button>
        </template>
        <template v-else-if="mode === 'view' && ticket">
          <MoreActionsMenu :actions="menuActions" @action="handleMenuAction" />
        </template>
      </div>
    </div>

    <div class="layout-body">
      <div v-if="activeTab === 'details'" class="card-container">
        <CardTicketForm ref="ticketForm" v-model="draft" :createMode="mode === 'create'" :editMode="mode === 'edit'" />
      </div>

      <div v-if="activeTab === 'receivables'" class="receivables-container">
        <CustomTable
          :columns="visibleReceivableColumnsFiltered"
          :rows="receivables"
          :sticky-first-column="true"
          :loading="isLoadingReceivables"
          :is-empty="receivables.length === 0"
          :sort-by="receivableSortBy"
          :sort-order="receivableSortOrder"
          @sort="handleReceivableSort"
        >
          <template #column-receivable="{ row }">
            <span class="cell-value name-text">Receivable #{{ row.receivableId }}</span>
          </template>

          <template #column-dateDue="{ row }">
            <span class="cell-value">{{ formatUtils.formatDate(row.dateDue) }}</span>
          </template>

          <template #column-status="{ row }">
            <StatusBadge :status="calculateReceivableStatus(row)" />
          </template>

          <template #column-amountDue="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCents) }}</span>
          </template>

          <template #column-collected="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsCollected) }}</span>
          </template>

          <template #column-cancelled="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsCancelled) }}</span>
          </template>

          <template #column-balance="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsBalance) }}</span>
          </template>

          <template #column-actions="{ row }">
            <button
              class="button button-small"
              @click.stop="openModal('create-payment', { receivable: row })"
              :disabled="row.amountCentsBalance === 0">
              Process Payment
            </button>
          </template>

        </CustomTable>
      </div>

      <div v-if="activeTab === 'payments'" class="payments-container">
        <CustomTable
          :columns="visiblePaymentColumnsFiltered"
          :rows="payments"
          :sticky-first-column="true"
          :loading="isLoadingPayments"
          :is-empty="payments.length === 0"
          :sort-by="paymentSortBy"
          :sort-order="paymentSortOrder"
          @sort="handlePaymentSort"
        >
          <template #column-payment="{ row }">
            <span class="cell-value name-text">Payment #{{ row.paymentId }}</span>
          </template>

          <template #column-datePaid="{ row }">
            <span class="cell-value">{{ formatUtils.formatDate(row.datePaid) }}</span>
          </template>

          <template #column-paymentType="{ row }">
            <span class="cell-value">{{ row.paymentType }}</span>
          </template>

          <template #column-paymentMethod="{ row }">
            <span class="cell-value">{{ formatPaymentMethod(row.paymentMethod) }}</span>
          </template>

          <template #column-amountPaid="{ row }">
            <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsPaid) }}</span>
          </template>

          <template #column-amountRefunded="{ row }">
            <span v-if="row.amountCentsRefunded > 0" class="cell-value refund-cell">
              {{ formatUtils.formatCurrency(row.amountCentsRefunded) }}
              <Tooltip v-if="row.dateRefunded" :text="getRefundTooltip(row)" position="top">
                <InformationOutlineIcon :size="14" class="refund-info-icon" />
              </Tooltip>
            </span>
            <span v-else class="no-data">No refund</span>
          </template>

          <template #column-actions="{ row }">
            <button
              v-if="isAdmin"
              class="button button-small"
              @click.stop="openRefundModal(row)"
              :disabled="getNetPaymentAmount(row) === 0">
              Refund Payment
            </button>
          </template>
        </CustomTable>
      </div>
    </div>

    <div v-if="activeTab !== 'details'" class="layout-footer">
      <PaginationControl v-if="activeTab === 'receivables'"
        :current-page="receivablesCurrentPage"
        :total-items="totalReceivables"
        :items-per-page="receivablesItemsPerPage"
        @page-change="handleReceivablesPageChange"
      />
      <PaginationControl v-if="activeTab === 'payments'"
        :current-page="paymentsCurrentPage"
        :total-items="totalPayments"
        :items-per-page="paymentsItemsPerPage"
        @page-change="handlePaymentsPageChange"
      />
      <div v-if="ticket" class="footer-totals">
        <span class="footer-total-item">Paid: <strong>{{ formatUtils.formatCurrency(ticket.amountCentsPaid) }}</strong></span>
        <span class="footer-total-item">Refunded: <strong>{{ formatUtils.formatCurrency(ticket.amountCentsRefunded) }}</strong></span>
        <span class="footer-total-item">Collected: <strong>{{ formatUtils.formatCurrency(ticket.amountCentsCollected) }}</strong></span>
      </div>
    </div>

    <!-- Modals -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <CreatePaymentModal v-if="modal.id == 'create-payment'" :receivable="modal.data.receivable" @payment-created="handlePaymentCreated" />
            <RefundPaymentModal v-else-if="modal.id == 'refund-payment'" :payment="modal.data.payment" @close="closeModal" @refunded="handlePaymentRefunded" />
            <CancelItemModal v-else-if="modal.id == 'cancel-item'" :ticketItem="modal.data.ticketItem" :ticket="ticket" :receivables="draft.receivables" @cancelled="handleItemCancelled" />
            <CancelTicketModal v-else-if="modal.id == 'cancel-ticket'" :ticketId="ticketId" @close="closeModal" @cancelled="handleTicketCancelled" />
            <DeleteTicketModal v-else-if="modal.id == 'delete-ticket'" :ticketId="ticketId" @close="closeModal" @deleted="handleTicketDeleted" />
          </transition>
        </div>
      </div>
    </transition>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/app/AppLayout.vue';
import TabBar from '@/components/common/TabBar.vue';
import CardTicketForm from '@/components/custom/CardTicketForm.vue';
import StatusBadge from '@/components/common/StatusBadge.vue';
import CustomTable from '@/components/common/CustomTable.vue';
import PaginationControl from '@/components/common/PaginationControl.vue';
import MoreActionsMenu from '@/components/common/MoreActionsMenu.vue';
import CreatePaymentModal from '@/modals/CreatePaymentModal.vue';
import RefundPaymentModal from '@/modals/RefundPaymentModal.vue';
import CancelItemModal from '@/modals/CancelItemModal.vue';
import CancelTicketModal from '@/modals/CancelTicketModal.vue';
import DeleteTicketModal from '@/modals/DeleteTicketModal.vue';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { calculateReceivableStatus } from '@/utils/formatters';
import { getLocalToday } from '@/utils/dateUtils';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';
import { jsPDF } from 'jspdf';
import Tooltip from '@/components/common/Tooltip.vue';
import InformationOutlineIcon from 'vue-material-design-icons/InformationOutline.vue';

export default {
  name: 'TicketRecordView',
  computed: {
    ticketId() {
      return this.$route.params.id;
    },
    ticketLink() {
      return '/tickets/' + this.ticketId;
    },
    breadcrumbs() {
      return [
        { label: 'Tickets', link: '/tickets' },
        { label: this.ticketDisplayName || 'Ticket', link: this.ticketLink }
      ];
    },
    ticketDisplayName() {
      if (this.mode === 'create') return 'New Ticket';
      if (!this.ticket) return 'Loading...';
      return `Ticket #` + this.ticket.ticketId;
    },
    availableTabs() {
      const isCreateMode = this.mode === 'create';
      return [
        { key: 'details', label: 'Details', disabled: false },
        { key: 'receivables', label: 'Receivables', disabled: isCreateMode },
        { key: 'payments', label: 'Payments', disabled: isCreateMode }
      ];
    },
    activeTab() {
      const tab = this.$route.params.tab;
      const validTabs = this.availableTabs.map(t => t.key);
      if (tab && validTabs.includes(tab)) {
        return tab;
      }
      return 'details';
    },
    visibleReceivableColumnsFiltered() {
      return this.receivableColumns.filter(col => {
        // Always show first column (receivable)
        if (col.key === 'receivable') return true;
        // Always show actions column
        if (col.key === 'actions') return true;
        // Show column if visibility is not set (default true) or explicitly true
        return this.receivableColumnVisibility[col.key] !== false;
      });
    },
    visiblePaymentColumnsFiltered() {
      return this.paymentColumns.filter(col => {
        // Always show first column (payment)
        if (col.key === 'payment') return true;
        // Always show actions column
        if (col.key === 'actions') return true;
        // Show column if visibility is not set (default true) or explicitly true
        return this.paymentColumnVisibility[col.key] !== false;
      });
    },
    isAdmin() {
      return this.currentUser && this.currentUser.isAdmin;
    },
    menuActions() {
      const isCancelled = this.ticket?.isCancelled;
      return [
        { key: 'print-invoice', label: 'Print Invoice' },
        {
          key: 'edit-ticket',
          label: 'Edit Ticket',
          disabled: !this.isAdmin || isCancelled,
          tooltip: isCancelled ? 'Cannot edit a cancelled ticket' : (!this.isAdmin ? 'Only admins can edit tickets' : null)
        },
        { key: 'cancel-ticket', label: isCancelled ? 'Re-cancel Ticket' : 'Cancel Ticket', danger: true, disabled: isCancelled && !(this.ticket?.amountCentsBalance > 0), tooltip: isCancelled && !(this.ticket?.amountCentsBalance > 0) ? 'Ticket is already cancelled with no balance' : null },
        { key: 'delete-ticket', label: 'Delete Ticket', danger: true, disabled: !this.isAdmin, tooltip: !this.isAdmin ? 'Only admins can delete tickets' : undefined }
      ];
    }
  },
  data() {
    return {
      ticket: null,
      isLoading: false,
      mode: 'view',
      draft: {},
      originalDraft: null,
      practice: null,
      currentUser: null,
      formatUtils,
      // Receivables state
      receivables: [],
      totalReceivables: 0,
      receivablesCurrentPage: 1,
      receivablesItemsPerPage: 20,
      isLoadingReceivables: false,
      receivableSortBy: 'dateDue',
      receivableSortOrder: 'asc',
      receivableColumnVisibility: {},
      receivableColumns: [
        { key: 'receivable', label: 'Receivable ID', width: '200px' },
        { key: 'dateDue', label: 'Date Due', width: '200px', sortable: true, sortKey: 'dateDue' },
        { key: 'status', label: 'Status', width: '200px' },
        { key: 'amountDue', label: 'Amount Due', width: '200px', sortable: true, sortKey: 'amountCents' },
        { key: 'collected', label: 'Amount Collected', width: '200px', sortable: true, sortKey: 'amountCentsCollected' },
        { key: 'cancelled', label: 'Amount Cancelled', width: '200px', sortable: true, sortKey: 'amountCentsCancelled' },
        { key: 'balance', label: 'Current Balance', width: '200px', sortable: true, sortKey: 'amountCentsBalance' },
        { key: 'actions', label: '', width: '180px' }
      ],
      // Payments state
      payments: [],
      totalPayments: 0,
      paymentsCurrentPage: 1,
      paymentsItemsPerPage: 20,
      isLoadingPayments: false,
      paymentSortBy: 'datePaid',
      paymentSortOrder: 'asc',
      paymentColumnVisibility: {},
      paymentColumns: [
        { key: 'payment', label: 'Payment ID', width: '180px' },
        { key: 'datePaid', label: 'Date Paid', width: '150px', sortable: true, sortKey: 'datePaid' },
        { key: 'paymentType', label: 'Payment Type', width: '180px' },
        { key: 'paymentMethod', label: 'Payment Method', width: '150px' },
        { key: 'amountPaid', label: 'Amount Paid', width: '200px', sortable: true, sortKey: 'amountCentsPaid' },
        { key: 'amountRefunded', label: 'Amount Refunded', width: '200px', sortable: true, sortKey: 'amountCentsRefunded' },
        { key: 'actions', label: '', width: '180px' }
      ],
      modal: {
        open: false,
        id: '',
        data: {}
      }
    };
  },
  watch: {
    receivablesCurrentPage() {
      this.loadReceivables();
    },
    paymentsCurrentPage() {
      this.loadPayments();
    },
    activeTab: {
      handler(newTab) {
        if (newTab === 'receivables') {
          this.loadReceivables();
        } else if (newTab === 'payments') {
          this.loadPayments();
        }
      },
      immediate: true
    },
    receivableSortBy() {
      this.receivablesCurrentPage = 1;
      this.loadReceivables();
    },
    receivableSortOrder() {
      this.receivablesCurrentPage = 1;
      this.loadReceivables();
    },
    paymentSortBy() {
      this.paymentsCurrentPage = 1;
      this.loadPayments();
    },
    paymentSortOrder() {
      this.paymentsCurrentPage = 1;
      this.loadPayments();
    }
  },
  created() {
    this.loadReceivableColumnVisibility();
    this.loadPaymentColumnVisibility();
    if (this.ticketId === 'new') {
      this.enterCreateMode();
    } else {
      this.load();
    }
  },
  methods: {
    async load() {
      try {
        this.isLoading = true;

        this.currentUser = queryClient.getQueryData(['user']) || null;

        const ticket = await queryClient.fetchQuery({
          queryKey: ['ticket', this.ticketId],
          queryFn: () => api.fetchTicketById(this.ticketId)
        });

        const receivablesData = await queryClient.fetchQuery({
          queryKey: ['receivables', { ticketId: this.ticketId }],
          queryFn: () => api.fetchReceivables({ ticketId: this.ticketId })
        });

        const receivables = (receivablesData.receivables || []).sort((a, b) =>
          new Date(a.dateDue) - new Date(b.dateDue)
        );

        const amountDueToday = receivables.length > 0 ? receivables[0].amountCents : 0;
        const paymentPlanMonths = Math.max(0, receivables.length - 1);

        this.ticket = ticket;
        this.draft = {
          ...structuredClone(this.ticket),
          amountDueToday,
          paymentPlanMonths,
          receivables
        };

      } catch (err) {
        console.error('Failed to fetch ticket:', err);
        alertService.error('Failed to load ticket: ' + err.message);
        this.$router.push('/tickets');
      } finally {
        this.isLoading = false;
      }
    },
    handleTabChange(tabKey) {
      const basePath = `/tickets/${this.ticketId}`;
      if (tabKey === 'details') {
        this.$router.push(basePath);
      } else {
        this.$router.push(`${basePath}/${tabKey}`);
      }
    },
    enterCreateMode() {
      this.mode = 'create';
      this.draft = this.getEmptyDraft();

      const patientId = this.$route.query.patientId;
      if (patientId) {
        this.draft.patientId = parseInt(patientId);
      }

      const user = queryClient.getQueryData(['user']);
      if (user && user.userId) {
        this.draft.counselorId = user.userId;
      }
      this.currentUser = user || null;

      this.draft.dateTicketStart = getLocalToday();
    },
    cancelEdit() {
      this.$router.push('/tickets');
    },
    getEmptyDraft() {
      return {
        dateTicketStart: '',
        patientId: null,
        counselorId: null,
        ticketItems: [],
        amountDueToday: null,
        paymentPlanMonths: null,
        receivables: []
      };
    },
    transformDraftToPayload(draft) {
      const ticketItems = (draft.ticketItems || []).map(item => ({
        productId: item.productId,
        quantity: item.quantity || 0,
        durationMonths: item.durationMonths || 0,
        amountCents: item.amountCents || 0
      }));

      const receivables = this.$refs.ticketForm?.generatedReceivables || [];

      return {
        patientId: draft.patientId,
        counselorId: draft.counselorId,
        dateTicketStart: draft.dateTicketStart,
        ticketItems,
        receivables
      };
    },
    async saveChanges() {
      try {
        const payload = this.transformDraftToPayload(this.draft);
        const createdTicket = await api.createTicket(payload);
        alertService.success('Ticket created successfully');

        // Invalidate caches
        queryClient.invalidateQueries({ queryKey: ['tickets'] });
        queryClient.invalidateQueries({ queryKey: ['patients'] });
        queryClient.invalidateQueries({ queryKey: ['receivables'] });

        // Navigate to the created ticket in view mode
        this.$router.replace(`/tickets/${createdTicket.ticketId}`);
        this.mode = 'view';

        // Fetch the full ticket data with all computed fields
        const fullTicket = await queryClient.fetchQuery({
          queryKey: ['ticket', createdTicket.ticketId.toString()],
          queryFn: () => api.fetchTicketById(createdTicket.ticketId)
        });

        const receivablesData = await queryClient.fetchQuery({
          queryKey: ['receivables', { ticketId: createdTicket.ticketId }],
          queryFn: () => api.fetchReceivables({ ticketId: createdTicket.ticketId })
        });

        const receivables = (receivablesData.receivables || []).sort((a, b) =>
          new Date(a.dateDue) - new Date(b.dateDue)
        );

        const amountDueToday = receivables.length > 0 ? receivables[0].amountCents : 0;
        const paymentPlanMonths = Math.max(0, receivables.length - 1);

        this.ticket = fullTicket;
        this.draft = {
          ...structuredClone(this.ticket),
          amountDueToday,
          paymentPlanMonths,
          receivables
        };

      } catch (err) {
        console.error('Failed to create ticket:', err);
        alertService.error('Failed to create ticket: ' + err.message);
      }
    },
    async loadReceivables() {
      try {
        this.isLoadingReceivables = true;

        const offset = (this.receivablesCurrentPage - 1) * this.receivablesItemsPerPage;
        const response = await queryClient.fetchQuery({
          queryKey: ['receivables', { ticketId: this.ticketId, limit: this.receivablesItemsPerPage, offset, sortBy: this.receivableSortBy, sortOrder: this.receivableSortOrder }],
          queryFn: () => api.fetchReceivables({
            ticketId: this.ticketId,
            limit: this.receivablesItemsPerPage,
            offset,
            sortBy: this.receivableSortBy,
            sortOrder: this.receivableSortOrder
          })
        });

        this.receivables = response.receivables || [];
        this.totalReceivables = response.total || 0;

      } catch (err) {
        console.error('Failed to fetch receivables:', err);
        alertService.error('Failed to load receivables: ' + err.message);
        this.receivables = [];
        this.totalReceivables = 0;
      } finally {
        this.isLoadingReceivables = false;
      }
    },
    async loadPayments() {
      try {
        this.isLoadingPayments = true;

        const offset = (this.paymentsCurrentPage - 1) * this.paymentsItemsPerPage;
        const response = await queryClient.fetchQuery({
          queryKey: ['payments', { ticketId: this.ticketId, limit: this.paymentsItemsPerPage, offset, sortBy: this.paymentSortBy, sortOrder: this.paymentSortOrder }],
          queryFn: () => api.fetchPayments({
            ticketId: this.ticketId,
            limit: this.paymentsItemsPerPage,
            offset,
            sortBy: this.paymentSortBy,
            sortOrder: this.paymentSortOrder
          })
        });

        this.payments = response.payments || [];
        this.totalPayments = response.total || 0;

      } catch (err) {
        console.error('Failed to fetch payments:', err);
        alertService.error('Failed to load payments: ' + err.message);
        this.payments = [];
        this.totalPayments = 0;
      } finally {
        this.isLoadingPayments = false;
      }
    },
    handleReceivablesPageChange(page) {
      this.receivablesCurrentPage = page;
    },
    handlePaymentsPageChange(page) {
      this.paymentsCurrentPage = page;
    },
    handleReceivableSort({ sortBy, sortOrder }) {
      this.receivableSortBy = sortBy;
      this.receivableSortOrder = sortOrder;
    },
    handlePaymentSort({ sortBy, sortOrder }) {
      this.paymentSortBy = sortBy;
      this.paymentSortOrder = sortOrder;
    },
    getRefundTooltip(row) {
      let text = `Refunded on ${formatUtils.formatDate(row.dateRefunded)}`;
      if (row.reasonRefunded) {
        text += `\nReason: ${row.reasonRefunded}`;
      }
      return text;
    },
    formatPaymentMethod(method) {
      if (!method) return 'Unknown';
      const labels = {
        cash: 'Cash',
        check: 'Check',
        mastercard: 'Mastercard',
        visa: 'Visa',
        amex: 'AMX',
        discover: 'Discover',
        cashiers_check: 'Cashiers Check',
        agms: 'AGMS'
      };
      return labels[method] || method.charAt(0).toUpperCase() + method.slice(1);
    },
    openRefundModal(payment) {
      this.openModal('refund-payment', { payment });
    },
    openModal(id, data) {
      this.modal.open = true;

      setTimeout(() => {
        this.modal.id = id;
        this.modal.data = data;
      })
    },
    closeModal() {
      this.modal.id = '';

      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      const classList = event.target.classList;
      if (classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    handlePaymentCreated() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', this.ticketId] });
      this.loadReceivables();
      this.loadPayments();
      alertService.success('Payment processed successfully');
    },
    handlePaymentRefunded() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', this.ticketId] });
      this.loadReceivables();
      this.loadPayments();
      alertService.success('Payment refunded successfully');
    },
    handleCancelItem(ticketItem) {
      this.openModal('cancel-item', { ticketItem });
    },
    handleMenuAction(actionKey) {
      if (actionKey === 'cancel-ticket') {
        this.openModal('cancel-ticket', {});
      } else if (actionKey === 'delete-ticket') {
        this.openModal('delete-ticket', {});
      } else if (actionKey === 'print-invoice') {
        this.generateInvoicePDF();
      } else if (actionKey === 'edit-ticket') {
        this.enterEditMode();
      }
    },
    enterEditMode() {
      if (!this.isAdmin) return;
      this.originalDraft = structuredClone(this.draft);
      this.mode = 'edit';
    },
    cancelEditMode() {
      this.draft = this.originalDraft;
      this.originalDraft = null;
      this.mode = 'view';
    },
    async saveTicketChanges() {
      try {
        // Prepare update payload - only editable fields
        const updatePayload = {
          dateTicketStart: this.draft.dateTicketStart,
          counselorId: this.draft.counselorId,
          ticketItems: (this.draft.ticketItems || []).map(item => ({
            ticketItemId: item.ticketItemId,
            productId: item.productId,
            quantity: item.quantity,
            durationMonths: item.durationMonths,
            amountCents: item.amountCents
          }))
        };

        await api.updateTicket(this.ticketId, updatePayload);
        alertService.success('Ticket updated successfully');

        // Invalidate caches and reload
        queryClient.invalidateQueries({ queryKey: ['tickets'] });
        queryClient.invalidateQueries({ queryKey: ['ticket', this.ticketId] });
        queryClient.invalidateQueries({ queryKey: ['receivables'] });

        this.mode = 'view';
        this.originalDraft = null;
        await this.load();
      } catch (err) {
        console.error('Failed to update ticket:', err);
        alertService.error('Failed to update ticket: ' + err.message);
      }
    },
    handleTicketCancelled() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', this.ticketId] });
      this.load();
      alertService.success('Ticket cancelled successfully');
    },
    handleTicketDeleted() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      alertService.success('Ticket deleted successfully');
      this.$router.push('/tickets');
    },
    handleItemCancelled() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', this.ticketId] });
      this.load();
      this.loadReceivables();
      alertService.success('Ticket item cancelled successfully');
    },
    getNetPaymentAmount(payment) {
      return payment.amountCentsPaid - (payment.amountCentsRefunded || 0);
    },
    calculateReceivableStatus(receivable) {
      return calculateReceivableStatus(receivable);
    },
    async generateInvoicePDF() {
      try {
        if (!this.practice) {
          const practiceId = api.getPracticeId();
          this.practice = await queryClient.fetchQuery({
            queryKey: ['practice', practiceId],
            queryFn: () => api.fetchPracticeById(practiceId)
          });
        }

        const patient = await queryClient.fetchQuery({
          queryKey: ['patient', this.ticket.patientId],
          queryFn: () => api.fetchPatientById(this.ticket.patientId)
        });

        const productsMap = new Map();
        for (const item of this.draft.ticketItems) {
          try {
            const product = await queryClient.fetchQuery({
              queryKey: ['product', item.productId],
              queryFn: () => api.fetchProductById(item.productId)
            });
            productsMap.set(item.productId, product);
          } catch (err) {
            console.error('Failed to fetch product:', err);
          }
        }

        // Collect unique diagnosis codes from all products
        const diagnosisCodesSet = new Set();
        for (const product of productsMap.values()) {
          if (product.diagnosisCodes && product.diagnosisCodes.trim()) {
            const codes = product.diagnosisCodes.split(',').map(c => c.trim()).filter(c => c);
            codes.forEach(code => diagnosisCodesSet.add(code));
          }
        }
        const uniqueDiagnosisCodes = Array.from(diagnosisCodesSet);

        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'pt',
          format: 'letter'
        });

        // Load Rubik font from CDN
        try {
          const fontUrl = 'https://cdn.jsdelivr.net/fontsource/fonts/rubik@latest/latin-400-normal.ttf';
          const fontResponse = await fetch(fontUrl);
          if (!fontResponse.ok) throw new Error('Failed to fetch regular font');
          const fontBuffer = await fontResponse.arrayBuffer();
          const fontBytes = new Uint8Array(fontBuffer);
          let fontBase64 = '';
          for (let i = 0; i < fontBytes.length; i++) {
            fontBase64 += String.fromCharCode(fontBytes[i]);
          }
          fontBase64 = btoa(fontBase64);
          doc.addFileToVFS('Rubik-Regular.ttf', fontBase64);
          doc.addFont('Rubik-Regular.ttf', 'Rubik', 'normal');

          const fontBoldUrl = 'https://cdn.jsdelivr.net/fontsource/fonts/rubik@latest/latin-700-normal.ttf';
          const fontBoldResponse = await fetch(fontBoldUrl);
          if (!fontBoldResponse.ok) throw new Error('Failed to fetch bold font');
          const fontBoldBuffer = await fontBoldResponse.arrayBuffer();
          const fontBoldBytes = new Uint8Array(fontBoldBuffer);
          let fontBoldBase64 = '';
          for (let i = 0; i < fontBoldBytes.length; i++) {
            fontBoldBase64 += String.fromCharCode(fontBoldBytes[i]);
          }
          fontBoldBase64 = btoa(fontBoldBase64);
          doc.addFileToVFS('Rubik-Bold.ttf', fontBoldBase64);
          doc.addFont('Rubik-Bold.ttf', 'Rubik', 'bold');

          doc.setFont('Rubik', 'normal');
        } catch (fontErr) {
          console.warn('Failed to load Rubik font, falling back to helvetica:', fontErr);
          doc.setFont('helvetica', 'normal');
        }

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 40;
        const contentWidth = pageWidth - (margin * 2);
        let y = 0;

        // Helper to check if we need a new page and add one if necessary
        const checkPageBreak = (requiredHeight) => {
          if (y + requiredHeight > pageHeight - margin) {
            doc.addPage();
            y = margin;
            return true;
          }
          return false;
        };

        // ========== HEADER - Light gray bar ==========
        const headerHeight = 60;
        doc.setFillColor(248, 248, 248);
        doc.rect(0, 0, pageWidth, headerHeight, 'F');

        // Invoice title (left side, black text)
        try { doc.setFont('Rubik', 'bold'); } catch { doc.setFont('helvetica', 'bold'); }
        doc.setFontSize(18);
        doc.setTextColor(0, 0, 0);
        doc.text('INVOICE', margin, 30);

        // Invoice details (subtitle, dark gray)
        try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
        doc.setFontSize(12);
        doc.setTextColor(80, 80, 80);
        doc.text(`#${this.ticket.ticketId} - ${formatUtils.formatDate(this.ticket.dateTicketStart)}`, margin, 48);

        // Logo on right side of header (if available)
        if (this.practice.logo) {
          try {
            const logoImg = new Image();
            await new Promise((resolve, reject) => {
              logoImg.onload = resolve;
              logoImg.onerror = reject;
              logoImg.src = this.practice.logo;
            });

            const maxLogoHeight = 40;
            const maxLogoWidth = 120;
            let logoWidth = logoImg.width;
            let logoHeight = logoImg.height;

            if (logoHeight > maxLogoHeight) {
              logoWidth = Math.round((logoWidth * maxLogoHeight) / logoHeight);
              logoHeight = maxLogoHeight;
            }
            if (logoWidth > maxLogoWidth) {
              logoHeight = Math.round((logoHeight * maxLogoWidth) / logoWidth);
              logoWidth = maxLogoWidth;
            }

            const logoX = pageWidth - margin - logoWidth;
            const logoY = (headerHeight - logoHeight) / 2;
            doc.addImage(this.practice.logo, 'PNG', logoX, logoY, logoWidth, logoHeight);
          } catch (logoErr) {
            console.warn('Failed to load logo:', logoErr);
          }
        }

        y = headerHeight + 20;

        // ========== ROW 1: Three Columns ==========
        const columnGap = 12;
        const colWidth = (contentWidth - columnGap * 2) / 3;

        const col1X = margin;
        const col2X = margin + colWidth + columnGap;
        const col3X = col2X + colWidth + columnGap;

        const rowStartY = y;

        const boxPadding = 10;
        const lineHeight = 12;
        const headerLineHeight = 28;
        const textWidth = colWidth - boxPadding * 2;
        const phoneLabelWidth = 18;

        // Helper to count wrapped lines
        const countLines = (text, maxWidth) => {
          if (!text) return 1;
          return doc.splitTextToSize(String(text), maxWidth).length;
        };

        // Helper to render text with full wrapping
        const renderWrappedText = (text, x, currentY, maxWidth) => {
          const lines = doc.splitTextToSize(String(text || ''), maxWidth);
          for (let i = 0; i < lines.length; i++) {
            doc.text(lines[i], x, currentY + (i * lineHeight));
          }
          return lines.length;
        };

        // ---------- Prepare all data and count lines ----------

        // Column 1: Practice Info - chunked into groups
        const practiceLines = [];
        // Chunk 1: Address
        if (this.practice.addressStreet) practiceLines.push({ value: this.practice.addressStreet });
        if (this.practice.addressCity || this.practice.addressState || this.practice.addressZip) {
          const cityStateZip = [this.practice.addressCity, this.practice.addressState, this.practice.addressZip].filter(v => v).join(', ');
          practiceLines.push({ value: cityStateZip });
        }
        // Chunk 2: Contact info (no gap - keep it compact)
        if (this.practice.email) practiceLines.push({ value: this.practice.email });
        if (this.practice.website) practiceLines.push({ value: this.practice.website });
        if (this.practice.phone) practiceLines.push({ value: formatUtils.formatPhoneNumber(this.practice.phone) });

        // Provider info with bold labels (Chunk 3)
        const providerLines = [];
        if (this.practice.doctorName) providerLines.push({ label: 'Doctor', value: this.practice.doctorName });
        if (this.practice.taxId) providerLines.push({ label: 'Tax ID', value: this.practice.taxId });
        if (this.practice.licenseNumber) providerLines.push({ label: 'License #', value: this.practice.licenseNumber });
        if (this.practice.npiNumber) providerLines.push({ label: 'NPI #', value: this.practice.npiNumber });

        const sectionGap = providerLines.length > 0 ? 10 : 0;

        // Column 2: Patient Info
        const patientLines = [];
        patientLines.push({ value: `${patient.firstName} ${patient.lastName}` });
        if (patient.dateOfBirth) patientLines.push({ label: 'DOB:', value: formatUtils.formatDate(patient.dateOfBirth) });
        if (patient.addressStreet) patientLines.push({ value: patient.addressStreet });
        if (patient.addressCity || patient.addressState || patient.addressZip) {
          const cityStateZip = [patient.addressCity, patient.addressState, patient.addressZip].filter(v => v).join(', ');
          patientLines.push({ value: cityStateZip });
        }
        if (patient.mobilePhone) patientLines.push({ label: 'M:', value: formatUtils.formatPhoneNumber(patient.mobilePhone) });
        if (patient.homePhone) patientLines.push({ label: 'H:', value: formatUtils.formatPhoneNumber(patient.homePhone) });
        if (patient.workPhone) patientLines.push({ label: 'W:', value: formatUtils.formatPhoneNumber(patient.workPhone) });

        // Column 3: Diagnosis Codes
        const diagnosisList = uniqueDiagnosisCodes.length > 0 ? uniqueDiagnosisCodes : [];

        // Calculate total line count for each column (accounting for wrapping)
        doc.setFontSize(8);
        let col1LineCount = 0;
        for (const line of practiceLines) {
          col1LineCount += countLines(line.value, textWidth);
        }
        col1LineCount += sectionGap > 0 ? 0.75 : 0; // Gap before provider info
        for (const line of providerLines) {
          col1LineCount += countLines(line.value, textWidth - 60);
        }

        let col2LineCount = 0;
        for (const line of patientLines) {
          const maxW = line.label ? textWidth - phoneLabelWidth : textWidth;
          col2LineCount += countLines(line.value, maxW);
        }

        let col3LineCount = 0;
        if (diagnosisList.length > 0) {
          for (const code of diagnosisList) {
            col3LineCount += countLines(code, textWidth);
          }
        } else {
          col3LineCount = 1;
        }

        // Calculate box height based on max content
        const col1ContentHeight = headerLineHeight + (col1LineCount * lineHeight) + sectionGap;
        const col2ContentHeight = headerLineHeight + (col2LineCount * lineHeight);
        const col3ContentHeight = headerLineHeight + (col3LineCount * lineHeight);
        const maxContentHeight = Math.max(col1ContentHeight, col2ContentHeight, col3ContentHeight);
        const boxHeight = boxPadding * 2 + maxContentHeight;

        // ---------- Column 1: Practice Info (no box, just content) ----------
        let col1Y = rowStartY;

        // Header
        try { doc.setFont('Rubik', 'bold'); } catch { doc.setFont('helvetica', 'bold'); }
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text('FROM', col1X, col1Y + 9);
        col1Y += 24;

        // Practice name
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        const practiceNameHeader = this.practice.name || 'Practice Name';
        const practiceNameWrapped = doc.splitTextToSize(practiceNameHeader, textWidth);
        doc.text(practiceNameWrapped[0] || '', col1X, col1Y);
        col1Y += 16;

        // Practice general info (no labels)
        try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
        doc.setFontSize(8);
        doc.setTextColor(50, 50, 50);

        for (const line of practiceLines) {
          const linesRendered = renderWrappedText(line.value, col1X, col1Y, textWidth);
          col1Y += linesRendered * lineHeight;
        }

        // Gap before provider info
        if (providerLines.length > 0) {
          col1Y += sectionGap;

          // Provider info with labels
          for (const line of providerLines) {
            try { doc.setFont('Rubik', 'bold'); } catch { doc.setFont('helvetica', 'bold'); }
            doc.setTextColor(100, 100, 100);
            doc.text(line.label + ':', col1X, col1Y);
            const labelW = doc.getTextWidth(line.label + ': ');

            try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
            doc.setTextColor(50, 50, 50);
            const linesRendered = renderWrappedText(line.value, col1X + labelW + 4, col1Y, textWidth - labelW - 4);
            col1Y += linesRendered * lineHeight;
          }
        }

        // ---------- Column 2: Patient Info ----------
        let col2Y = rowStartY;

        // Header
        try { doc.setFont('Rubik', 'bold'); } catch { doc.setFont('helvetica', 'bold'); }
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text('BILL TO', col2X, col2Y + 9);
        col2Y += 24;

        // Patient name
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(`${patient.firstName} ${patient.lastName}`, col2X, col2Y);
        col2Y += 16;

        try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
        doc.setFontSize(8);
        doc.setTextColor(50, 50, 50);

        for (const line of patientLines.slice(1)) { // Skip first line (name already rendered)
          if (line.label) {
            doc.setTextColor(100, 100, 100);
            doc.text(line.label, col2X, col2Y);
            doc.setTextColor(50, 50, 50);
            const labelW = doc.getTextWidth(line.label + ' ');
            const linesRendered = renderWrappedText(line.value, col2X + labelW, col2Y, textWidth - labelW);
            col2Y += linesRendered * lineHeight;
          } else {
            const linesRendered = renderWrappedText(line.value, col2X, col2Y, textWidth);
            col2Y += linesRendered * lineHeight;
          }
        }

        // ---------- Column 3: Diagnosis ----------
        let col3Y = rowStartY;

        // Header
        try { doc.setFont('Rubik', 'bold'); } catch { doc.setFont('helvetica', 'bold'); }
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text('DIAGNOSIS', col3X, col3Y + 9);
        col3Y += 24;

        try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
        doc.setFontSize(8);
        doc.setTextColor(50, 50, 50);

        if (diagnosisList.length > 0) {
          for (const code of diagnosisList) {
            const linesRendered = renderWrappedText(code, col3X, col3Y, textWidth);
            col3Y += linesRendered * lineHeight;
          }
        } else {
          doc.setTextColor(150, 150, 150);
          doc.text('No diagnosis codes', col3X, col3Y);
          col3Y += lineHeight;
        }

        // Move y to below the boxes
        y = rowStartY + boxHeight + 25;

        // ========== ROW 2: Ticket Items Table ==========

        // Calculate total
        let totalCents = 0;
        for (const item of this.draft.ticketItems) {
          totalCents += item.amountCents || 0;
        }

        // Check if we have room for header + at least one row
        checkPageBreak(60);

        // Section header with total on right
        try { doc.setFont('Rubik', 'bold'); } catch { doc.setFont('helvetica', 'bold'); }
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text('Ticket Items', margin, y);
        doc.text('Total Cost: ' + formatUtils.formatCurrency(totalCents), pageWidth - margin, y, { align: 'right' });
        y += 16;

        // Table setup
        const tableColumns = [
          { key: 'num', label: '#', width: 30 },
          { key: 'name', label: 'Item', width: 120 },
          { key: 'dosePeriod', label: 'Dose / Period', width: 80 },
          { key: 'description', label: 'Description', width: 130 },
          { key: 'insuranceCodes', label: 'Codes', width: 80 },
          { key: 'price', label: 'Amount', width: 65, align: 'right' }
        ];

        const tableRowHeight = 24;
        const tableHeaderHeight = 22;
        const cellPadding = 6;

        // Helper to draw table header with light gray background
        const drawTicketItemsHeader = () => {
          // Light gray header background
          doc.setFillColor(248, 248, 248);
          doc.rect(margin, y, contentWidth, tableHeaderHeight, 'F');

          try { doc.setFont('Rubik', 'bold'); } catch { doc.setFont('helvetica', 'bold'); }
          doc.setFontSize(7);
          doc.setTextColor(0, 0, 0);

          let colX = margin;
          for (const col of tableColumns) {
            if (col.align === 'right') {
              doc.text(col.label.toUpperCase(), colX + col.width - cellPadding, y + 14, { align: 'right' });
            } else {
              doc.text(col.label.toUpperCase(), colX + cellPadding, y + 14);
            }
            colX += col.width;
          }
          y += tableHeaderHeight;
        };

        // Draw initial table header
        drawTicketItemsHeader();

        // Draw table rows
        try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
        doc.setFontSize(8);
        doc.setTextColor(55, 65, 81); // Gray-700

        for (let i = 0; i < this.draft.ticketItems.length; i++) {
          const item = this.draft.ticketItems[i];
          const product = productsMap.get(item.productId) || {};

          // Check if we need a new page for this row
          if (y + tableRowHeight > pageHeight - margin) {
            doc.addPage();
            y = margin;
            drawTicketItemsHeader();
            try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
            doc.setFontSize(8);
            doc.setTextColor(55, 65, 81); // Gray-700
          }

          let colX = margin;
          const rowY = y + 15;

          // Helper to safely render truncated text
          const renderCell = (text, x, width, align = 'left', color = null) => {
            if (color) {
              doc.setTextColor(color[0], color[1], color[2]);
            }
            const safeText = String(text || '-');
            const maxWidth = width - cellPadding * 2;
            const lines = doc.splitTextToSize(safeText, maxWidth);
            const displayText = lines[0] || '-';
            if (align === 'right') {
              doc.text(displayText, x + width - cellPadding, rowY, { align: 'right' });
            } else {
              doc.text(displayText, x + cellPadding, rowY);
            }
          };

          // # column
          doc.setTextColor(100, 100, 100);
          renderCell(String(i + 1), colX, tableColumns[0].width, 'left');
          colX += tableColumns[0].width;

          // Item name column
          doc.setTextColor(0, 0, 0);
          const itemName = item.productName || product.name || '-';
          renderCell(itemName, colX, tableColumns[1].width, 'left');
          colX += tableColumns[1].width;

          // Dose / Period column
          doc.setTextColor(50, 50, 50);
          const doseValue = product.dose || item.quantity || null;
          const periodValue = item.durationMonths || product.defaultDurationMonths || null;
          let dosePeriod = '-';
          if (doseValue && periodValue) {
            const monthLabel = periodValue === 1 ? 'mo' : 'mo';
            dosePeriod = `${doseValue} / ${periodValue} ${monthLabel}`;
          } else if (doseValue) {
            dosePeriod = String(doseValue);
          } else if (periodValue) {
            const monthLabel = periodValue === 1 ? 'month' : 'months';
            dosePeriod = `${periodValue} ${monthLabel}`;
          }
          renderCell(dosePeriod, colX, tableColumns[2].width, 'left');
          colX += tableColumns[2].width;

          // Description column
          renderCell(product.description, colX, tableColumns[3].width, 'left');
          colX += tableColumns[3].width;

          // Insurance Codes column
          renderCell(product.insuranceCodes, colX, tableColumns[4].width, 'left');
          colX += tableColumns[4].width;

          // Price column (right aligned)
          doc.setTextColor(0, 0, 0);
          const price = formatUtils.formatCurrency(item.amountCents || 0);
          renderCell(price, colX, tableColumns[5].width, 'right');

          y += tableRowHeight;

          // Row bottom border
          doc.setDrawColor(200, 200, 200);
          doc.line(margin, y, pageWidth - margin, y);
        }

        // Draw bottom border line (no side borders)
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y, pageWidth - margin, y);

        y += 30;

        // ========== ROW 3: Payment Details ==========

        // Fetch payments for this ticket
        const paymentsData = await queryClient.fetchQuery({
          queryKey: ['payments', { ticketId: this.ticketId, limit: 100 }],
          queryFn: () => api.fetchPayments({ ticketId: this.ticketId, limit: 100 })
        });
        const ticketPayments = paymentsData.payments || [];

        // Calculate total paid
        let totalPaidCents = 0;
        for (const payment of ticketPayments) {
          totalPaidCents += (payment.amountCentsPaid || 0) - (payment.amountCentsRefunded || 0);
        }

        // Get receivables sorted by date
        const sortedReceivables = [...(this.draft.receivables || [])].sort((a, b) =>
          new Date(a.dateDue) - new Date(b.dateDue)
        );

        // Check if we have room for header + at least one row
        checkPageBreak(60);

        // Section header with total on right
        try { doc.setFont('Rubik', 'bold'); } catch { doc.setFont('helvetica', 'bold'); }
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text('Payments', margin, y);
        doc.text('Total Paid: ' + formatUtils.formatCurrency(totalPaidCents), pageWidth - margin, y, { align: 'right' });
        y += 16;

        // Payment Details table setup
        const paymentTableColumns = [
          { key: 'num', label: '#', width: 30 },
          { key: 'receivable', label: 'Scheduled', width: 230 },
          { key: 'payment', label: 'Payment Received', width: contentWidth - 260 }
        ];

        const paymentTableRowHeight = 24;
        const paymentTableHeaderHeight = 22;

        // Helper to draw payment table header with light gray background
        const drawPaymentHeader = () => {
          // Light gray header background
          doc.setFillColor(248, 248, 248);
          doc.rect(margin, y, contentWidth, paymentTableHeaderHeight, 'F');

          try { doc.setFont('Rubik', 'bold'); } catch { doc.setFont('helvetica', 'bold'); }
          doc.setFontSize(7);
          doc.setTextColor(0, 0, 0);

          let payColX = margin;
          for (const col of paymentTableColumns) {
            doc.text(col.label.toUpperCase(), payColX + cellPadding, y + 14);
            payColX += col.width;
          }
          y += paymentTableHeaderHeight;
        };

        // Draw initial table header
        drawPaymentHeader();

        // Draw payment rows
        try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);

        // Determine number of rows (max of receivables or payments)
        const maxRows = Math.max(sortedReceivables.length, ticketPayments.length);

        for (let i = 0; i < maxRows; i++) {
          const receivable = sortedReceivables[i];
          const payment = ticketPayments[i];

          // Check if we need a new page for this row
          if (y + paymentTableRowHeight > pageHeight - margin) {
            doc.addPage();
            y = margin;
            drawPaymentHeader();
            try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
            doc.setFontSize(8);
            doc.setTextColor(0, 0, 0);
          }

          let payColX = margin;
          const payRowY = y + 15;

          // # column
          doc.setTextColor(100, 100, 100);
          doc.text(String(i + 1), payColX + cellPadding, payRowY);
          payColX += paymentTableColumns[0].width;

          // Receivable column
          doc.setTextColor(0, 0, 0);
          if (receivable) {
            const receivableText = `${formatUtils.formatCurrency(receivable.amountCents)} due ${formatUtils.formatDate(receivable.dateDue)}`;
            const recLines = doc.splitTextToSize(receivableText, paymentTableColumns[1].width - cellPadding * 2);
            doc.text(recLines[0] || '-', payColX + cellPadding, payRowY);
          } else {
            doc.setTextColor(150, 150, 150);
            doc.text('-', payColX + cellPadding, payRowY);
          }
          payColX += paymentTableColumns[1].width;

          // Payment column
          if (payment) {
            const netAmount = (payment.amountCentsPaid || 0) - (payment.amountCentsRefunded || 0);
            doc.setTextColor(0, 0, 0);
            const paymentText = `${formatUtils.formatCurrency(netAmount)} via ${this.formatPaymentMethod(payment.paymentMethod)} on ${formatUtils.formatDate(payment.datePaid)}`;
            const payLines = doc.splitTextToSize(paymentText, paymentTableColumns[2].width - cellPadding * 2);
            doc.text(payLines[0], payColX + cellPadding, payRowY);
          } else {
            doc.setTextColor(150, 150, 150);
            doc.text(`Unpaid as of ${formatUtils.formatDate(getLocalToday())}`, payColX + cellPadding, payRowY);
          }

          y += paymentTableRowHeight;

          // Row bottom border
          doc.setDrawColor(200, 200, 200);
          doc.line(margin, y, pageWidth - margin, y);
        }

        // Draw bottom border line (no side borders)
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y, pageWidth - margin, y);

        y += 40;

        // ========== ROW 4: Summary & Signature ==========

        // Calculate amount due
        const amountDueCents = totalCents - totalPaidCents;
        const isPaidInFull = amountDueCents <= 0;

        // Layout - summary on left, signature on right
        const summaryBoxWidth = 180;
        const summaryBoxPadding = 14;
        const sigSectionWidth = 180;

        // Check if we have room for the summary section
        checkPageBreak(130);

        // Left side: Summary box
        const boxX = margin;
        const boxStartY = y;

        // Calculate box height
        const summaryLineHeight = 20;
        const summaryBoxHeight = summaryBoxPadding * 2 + (summaryLineHeight * 3) + 10 + (isPaidInFull ? 26 : 0);

        // Draw summary box with app styling (white bg, clean border, light gray accent)
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(224, 224, 224);
        doc.rect(boxX, boxStartY, summaryBoxWidth, summaryBoxHeight, 'FD');

        // Light gray accent bar at top
        doc.setFillColor(248, 248, 248);
        doc.rect(boxX, boxStartY, summaryBoxWidth, 4, 'F');

        // Summary content
        let summaryY = boxStartY + summaryBoxPadding + 16;
        const labelX = boxX + summaryBoxPadding;
        const amountX = boxX + summaryBoxWidth - summaryBoxPadding;

        try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);

        // Total Cost
        doc.text('Subtotal', labelX, summaryY);
        doc.setTextColor(0, 0, 0);
        doc.text(formatUtils.formatCurrency(totalCents), amountX, summaryY, { align: 'right' });
        summaryY += summaryLineHeight;

        // Total Paid
        doc.setTextColor(100, 100, 100);
        doc.text('Paid', labelX, summaryY);
        doc.setTextColor(0, 0, 0);
        doc.text(`-${formatUtils.formatCurrency(totalPaidCents)}`, amountX, summaryY, { align: 'right' });
        summaryY += 10;

        // Divider line
        doc.setDrawColor(200, 200, 200);
        doc.line(labelX, summaryY, amountX, summaryY);
        summaryY += summaryLineHeight;

        // Amount Due (bold, black color)
        try { doc.setFont('Rubik', 'bold'); } catch { doc.setFont('helvetica', 'bold'); }
        doc.setFontSize(11);
        doc.setTextColor(38, 38, 38); // #262626 - app primary text
        doc.text('Balance Due', labelX, summaryY);
        if (isPaidInFull) {
          doc.setTextColor(0, 0, 0); // Black for $0
          doc.text('$0.00', amountX, summaryY, { align: 'right' });
        } else {
          doc.setTextColor(0, 0, 0); // Black color
          doc.text(formatUtils.formatCurrency(amountDueCents), amountX, summaryY, { align: 'right' });
        }

        // "All sales are final" note
        summaryY += 6;
        try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text('All sales are final', labelX, summaryY);

        // PAID IN FULL badge (left aligned, black)
        if (isPaidInFull) {
          summaryY += 22;
          doc.setFillColor(240, 240, 240); // Light gray background
          doc.setDrawColor(100, 100, 100); // Gray border
          const badgeWidth = 90;
          const badgeX = labelX; // Left aligned
          doc.roundedRect(badgeX, summaryY - 12, badgeWidth, 18, 3, 3, 'FD');
          doc.setFontSize(8);
          doc.setTextColor(0, 0, 0); // Black text
          doc.text('PAID IN FULL', labelX + badgeWidth / 2, summaryY, { align: 'center' });
        }

        // Right side: Signature section (vertically centered with summary box)
        const sigCenterY = boxStartY + summaryBoxHeight / 2 + 8;
        const sigEndX = pageWidth - margin;
        const sigStartX = sigEndX - sigSectionWidth;

        // Signature line
        doc.setDrawColor(209, 213, 219); // Gray-300
        doc.line(sigStartX, sigCenterY, sigEndX, sigCenterY);

        // Signature label
        try { doc.setFont('Rubik', 'normal'); } catch { doc.setFont('helvetica', 'normal'); }
        doc.setFontSize(8);
        doc.setTextColor(107, 114, 128); // Gray-500
        doc.text('Patient Signature', sigStartX, sigCenterY + 14);

        // Date label (right side)
        doc.text('Date', sigEndX - 30, sigCenterY + 14, { align: 'center' });

        y = boxStartY + summaryBoxHeight + 25;

        doc.setTextColor(0, 0, 0);

        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');

      } catch (error) {
        console.error('Failed to generate invoice PDF:', error);
        alertService.error('Failed to generate invoice: ' + error.message);
      }
    },
    loadReceivableColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        const saved = localStorage.getItem(`shawncrm_columns_${practiceId}_receivables`);
        if (saved) {
          this.receivableColumnVisibility = JSON.parse(saved);
        } else {
          // Default columns: Date Due, Patient, Ticket, Status, Amount Due, Amount Collected, Current Balance
          // Note: Patient and Ticket columns don't exist in TicketRecordView, so they're excluded
          const defaultVisibleColumns = ['dateDue', 'status', 'amountDue', 'collected', 'balance'];
          this.receivableColumns.forEach(col => {
            this.receivableColumnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
          });
        }
      } catch (err) {
        console.error('Failed to load receivable column visibility:', err);
        // Default columns on error
        const defaultVisibleColumns = ['dateDue', 'status', 'amountDue', 'collected', 'balance'];
        this.receivableColumns.forEach(col => {
          this.receivableColumnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
        });
      }
    },
    loadPaymentColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        const saved = localStorage.getItem(`shawncrm_columns_${practiceId}_payments`);
        if (saved) {
          this.paymentColumnVisibility = JSON.parse(saved);
        } else {
          // Default columns: Date Paid, Payment Type, Patient, Ticket, Payment Method, Amount Paid, Amount Refunded
          // Note: Patient and Ticket columns don't exist in TicketRecordView, so they're excluded
          const defaultVisibleColumns = ['datePaid', 'paymentType', 'paymentMethod', 'amountPaid', 'amountRefunded'];
          this.paymentColumns.forEach(col => {
            this.paymentColumnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
          });
        }
      } catch (err) {
        console.error('Failed to load payment column visibility:', err);
        // Default columns on error
        const defaultVisibleColumns = ['datePaid', 'paymentType', 'paymentMethod', 'amountPaid', 'amountRefunded'];
        this.paymentColumns.forEach(col => {
          this.paymentColumnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
        });
      }
    }
  },
  components: { AppLayout, TabBar, CardTicketForm, StatusBadge, CustomTable, PaginationControl, MoreActionsMenu, CreatePaymentModal, RefundPaymentModal, CancelItemModal, CancelTicketModal, DeleteTicketModal, Tooltip, InformationOutlineIcon }
}
</script>

<style scoped>
.header-left {
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
  min-width: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ticket-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ticket-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.2;
}

.card-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}

.layout-header .button {
  white-space: nowrap;
}

/* Receivables and Payments tab containers */
.receivables-container,
.payments-container {
  height: 100%;
  width: 100%;
}

/* Cell styling */
.receivable-cell,
.payment-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.name-text {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-value {
  color: #333;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

.refund-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.refund-info-icon {
  color: #9ca3af;
  cursor: help;
}

.refund-info-icon:hover {
  color: #6b7280;
}

.footer-totals {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
  font-size: 13px;
  color: #6b7280;
}

.footer-total-item strong {
  color: #111827;
}

.refunded-amount {
  color: #dc2626;
}

/* Button styling */
.button-small {
  padding: 6px 12px;
  font-size: 13px;
  white-space: nowrap;
}

/* Actions column alignment and width */
:deep(th:last-child),
:deep(td:last-child) {
  width: 1%;
  white-space: nowrap;
  text-align: right;
}
</style>
